import { useEffect, useState } from "react";
import axios from "axios";

const apiUrl = process.env.REACT_APP_API_URL;

/**
 * ✅ Default config includes:
 * - theme (primary + onPrimary)
 * - branding.logo (showLogo + urls + size)
 * - branding.hero (supports both bgType + backgroundType for compatibility)
 * - website sections for micro-site preview (about/highlights/menu/gallery/policies/contact)
 * - keep existing contentSections for your future dynamic builder
 */
const DEFAULT_CONFIG = {
  enabled: true,

  theme: {
    primary: "#9df03d",
    onPrimary: "#05070a"
  },

  bookingForm: {
  requiredFields: ["name", "phone", "guests", "special_requests"], // multi-select
  showTimePicker: true,
  minGuests: 1,
  maxGuests: 10,
  allowSpecialRequests: true,
  allowInternalNotes: true,
  redirectAfterSubmissionUrl: ""
},

  branding: {
    logo: {
      showLogo: true,
      logoUrl: "https://netshine-assets.blr1.cdn.digitaloceanspaces.com/web-assets/logo-dark.png",
      lightLogoUrl: "https://netshine-assets.blr1.cdn.digitaloceanspaces.com/web-assets/logo-light.png",
      width: 180,
      height: 70
    },

    hero: {
      // ✅ Support both naming styles (your editor uses bgType)
      bgType: "image", // "color" | "image" | "video"
      backgroundType: "image", // alias (PreviewHero supports backgroundType)

      bgColor: "#05070a",
      mediaUrl: "",
      overlayColor: "#000000",
      overlayOpacity: 0.45,

      headline: "Reserve your table in seconds",
      headlineColor: "#ffffff",
      headlineFontSize: 48,

      subline: "Instant confirmation · Best seating · No booking fee",
      sublineColor: "#d1d5db",
      sublineFontSize: 16,

      badges: []
    }
  },

  bookingRules: {
    slotIntervalMins: 15,
    defaultDurationMins: 90,
    minGuests: 1,
    maxGuests: 20
  },

  // ✅ Your existing dynamic sections builder (keep it)
  contentSections: [
    { id: "menu", type: "menu", title: "Our Menu", enabled: true }
  ],

  // ✅ Dedicated micro-site sections for preview + editor (Restaurant Info)
  website: {
    sectionOrder: ["about", "highlights", "menu", "gallery", "policies", "contact"],

    about: {
      enabled: true,
      title: "About the Restaurant",
      subtitle:
        "Share your story: cuisine, inspiration, chef highlights, ambience and what makes you special.",
      contentHtml:
        "<p>Add your story here. Keep it warm, short and authentic — guests love context.</p>"
    },

    highlights: {
      enabled: true,
      title: "Highlights",
      subtitle: "Quick reasons guests love you",
      items: [
        { key: "h1", label: "Signature dishes", enabled: true },
        { key: "h2", label: "Outdoor seating", enabled: true },
        { key: "h3", label: "Family friendly", enabled: true }
      ]
    },

    menu: {
      enabled: true,
      title: "Menu",
      subtitle: "Explore the menu before you reserve",
      ctaLabel: "View Menu",
      menuUrl: ""
    },

    gallery: {
      enabled: true,
      title: "Gallery",
      subtitle: "Food · Ambience · Seating",
      images: [] // [{ url, alt, tag, order, enabled }]
    },

    policies: {
      enabled: true,
      title: "Reservation Policies",
      items: [
        {
          key: "p1",
          label: "Late arrivals",
          descriptionHtml: "<p>We hold tables for 15 minutes.</p>",
          enabled: true
        },
        {
          key: "p2",
          label: "Cancellations",
          descriptionHtml: "<p>Please cancel at least 2 hours in advance.</p>",
          enabled: true
        }
      ]
    },

    contact: {
      enabled: true,
      phone: "",
      whatsapp: "",
      email: "",
      website: "",
      address: "",
      mapUrl: ""
    }
  },

  flow: {
    autoAssignTable: true,
    allowTablePreference: true,
    allowModify: true,
    allowCancel: true,
    defaultStatus: "confirmed"
  },

  menu: {
    enabled: true,
    showPrices: true
  },

  eventsEnquiry: {
    enabled: false,
    eventTypes: []
  },

  seo: {
    slug: "",
    title: "",
    description: "",
    ogImage: ""
  },

  publish: {
    status: "draft",
    publishedAt: null
  }
};

/* ---------------------------------------
   Helpers
--------------------------------------- */

// ✅ Deep merge for nested objects (simple + safe for config)
function deepMerge(base, incoming) {
  if (!incoming) return base;
  const out = Array.isArray(base) ? [...base] : { ...base };

  Object.keys(incoming).forEach((k) => {
    const v = incoming[k];

    if (Array.isArray(v)) {
      out[k] = v; // arrays: overwrite (order matters)
      return;
    }

    if (v && typeof v === "object" && !Array.isArray(v)) {
      out[k] = deepMerge(base?.[k] || {}, v);
      return;
    }

    out[k] = v;
  });

  return out;
}

// ✅ Ensure hero backgroundType/bgType stay in sync
function normalizeHero(hero = {}) {
  const bgType = hero.bgType || hero.backgroundType || "image";
  return {
    ...hero,
    bgType,
    backgroundType: bgType
  };
}

const mergeConfig = (serverConfig = {}) => {
  const merged = deepMerge(DEFAULT_CONFIG, serverConfig || {});

  // normalize hero aliases
  merged.branding = merged.branding || {};
  merged.branding.hero = normalizeHero(merged.branding.hero || {});

  // ensure required nested nodes exist
  merged.branding.logo = deepMerge(DEFAULT_CONFIG.branding.logo, merged.branding.logo || {});
  merged.theme = deepMerge(DEFAULT_CONFIG.theme, merged.theme || {});
  merged.website = deepMerge(DEFAULT_CONFIG.website, merged.website || {});

  // ensure sectionOrder exists even if server removed it
  if (!Array.isArray(merged.website.sectionOrder) || merged.website.sectionOrder.length === 0) {
    merged.website.sectionOrder = [...DEFAULT_CONFIG.website.sectionOrder];
  }

  return merged;
};

// ✅ Robust extractor: supports many backend shapes safely
function extractConfig(data) {
  if (!data) return null;

  // Common: { success:true, config:{...} } OR { config:{...} }
  if (data.config && typeof data.config === "object") return data.config;

  // row shape: { widgetid, companyid, config: "{...json...}" }
  if (data.config && typeof data.config === "string") {
    try {
      return JSON.parse(data.config);
    } catch {
      return null;
    }
  }

  // { data: { config:{...} } }
  if (data.data?.config && typeof data.data.config === "object") return data.data.config;

  // { row: { config:{...} } }
  if (data.row?.config && typeof data.row.config === "object") return data.row.config;

  // { rows:[ { config:{...} } ] } or { rows:[ { config:"..." } ] }
  if (Array.isArray(data.rows) && data.rows[0]?.config) {
    const c = data.rows[0].config;
    if (typeof c === "object") return c;
    if (typeof c === "string") {
      try {
        return JSON.parse(c);
      } catch {
        return null;
      }
    }
  }

  return null;
}

export default function useReservationConfig() {
  const token = localStorage.getItem("token");

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const loadConfig = async () => {
    setLoading(true);

    const res = await axios.get(`${apiUrl}/pos/reservations/config`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    const serverConfig = extractConfig(res.data);
    setConfig(mergeConfig(serverConfig || {}));

    setLoading(false);
    setDirty(false);
  };

  useEffect(() => {
    loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateConfig = (next) => {
    setConfig(next);
    setDirty(true);
  };

  const saveDraft = async () => {
    if (!config) return;

    setSaving(true);

    // normalize hero aliases before save (keeps DB consistent)
    const nextToSave = {
      ...config,
      branding: {
        ...(config.branding || {}),
        hero: normalizeHero(config.branding?.hero || {})
      }
    };

    const payload = {
      config: nextToSave,
      is_active: true,
      config_name: "Reservation Experience"
    };

    const res = await axios.post(`${apiUrl}/pos/reservations/config`, payload, {
      headers: { Authorization: `Bearer ${token}` }
    });

    const savedConfig = extractConfig(res.data);
    if (savedConfig) {
      setConfig(mergeConfig(savedConfig));
    } else {
      await loadConfig();
    }

    setSaving(false);
    setDirty(false);
  };

  /**
   * ✅ Publish: since you currently only have /reservations/config (save)
   * We treat publish as a flag update + save.
   * This aligns with your room booking engine behavior.
   */
  const publish = async () => {
    if (!config) return;

    setSaving(true);

    const next = {
      ...config,
      publish: {
        status: "published",
        publishedAt: new Date().toISOString()
      }
    };

    const payload = {
      config: next,
      is_active: true,
      config_name: "Reservation Experience"
    };

    const res = await axios.post(`${apiUrl}/pos/reservations/config`, payload, {
      headers: { Authorization: `Bearer ${token}` }
    });

    const publishedConfig = extractConfig(res.data);
    if (publishedConfig) {
      setConfig(mergeConfig(publishedConfig));
    } else {
      await loadConfig();
    }

    setSaving(false);
    setDirty(false);
  };

  return {
    config,
    loading,
    saving,
    dirty,
    setConfig: updateConfig,
    saveDraft,
    publish,
    reload: loadConfig
  };
}
